import React from 'react';
import { Toast } from 'zarm';
import { StackPicker } from '@iyunbao/components';
import classnames from 'classnames';
import { createModel } from '../../utils/modelStore';

import UItem from '../item';
import { clearObj, deepAsset, deepCopy, isObject, getFormId, passiveRefresh } from '../../utils/tools';
import { BaseWidgetProps, OptionsProps, FetchProps } from '../../utils/type';

export interface PickerStackProps extends BaseWidgetProps {
  readOnly?: boolean;
  type?: string;
  value?: any;
  defaultValue?: any;
  label?: string;
  placeholder?: string;
  options?: OptionsProps;
  initFetch?: FetchProps;
  fetchOptsMember?: string;
  refreshOptionsPath?: string;
  fetchCache?: {
    name: string;
    expire?: any;
    params?: any;
  };
  dataBind?: string;
  valueMember: string;
  min?: number;
  max?: number;
  showAll?: boolean;
  showCode?: boolean;
  submitType?: 'button' | 'search';
  visible?: boolean;
  readonly?: boolean;
  className?: string;
  validate?: {
    rules: any[];
  };
  onCancel?: () => void;
  onMaskClick?: () => void;
}

interface PickerStackState {
  selected: any;
  defaultValue: any;
  prevValue?: any[];
  dataSource: OptionsProps;
}

let tempValue: any[] = [];
let isBreak = false;

const getColValue = (data: OptionsProps, value: any, valueMember: string, ...ags) => {
  if (value && Array.isArray(value) && value.length) {
    tempValue = value;
    return false;
  }

  // 首次进入，清空数组
  if (ags && ags.length <= 1) {
    tempValue = [];
    isBreak = false;
  }

  if (!(value && isObject(value))) {
    return false;
  }

  if (data && Array.isArray(data) && data.length) {
    const len = data.length;
    let i = 0;
    let item: any = null;

    for (; i < len; i += 1) {
      item = data[i];

      if (item[valueMember] === value[valueMember]) {
        isBreak = true;

        /* eslint-disable no-loop-func */
        if (ags && Array.isArray(ags) && ags.length) {
          ags.forEach((v) => {
            tempValue.push(v);
          });
        }

        tempValue.push(item);
      } else if (item.children) {
        getColValue(item.children, value, valueMember, ...ags, item);
      }

      if (isBreak) {
        break;
      }
    }
  }
};

const getValue = (props: PickerStackProps, dataSource?: OptionsProps) => {
  const { value, options, valueMember = 'value' } = props;
  let { defaultValue } = props;
  const isData = (options && Array.isArray(options) && options.length) || (dataSource && Array.isArray(dataSource) && dataSource.length);

  if (value) {
    defaultValue = value;
  }

  // 同步数据
  if (options && Array.isArray(options) && options.length) {
    getColValue(options, defaultValue, valueMember);
    // 异步接口请求
  } else if (dataSource && Array.isArray(dataSource) && dataSource.length) {
    getColValue(dataSource, defaultValue, valueMember);
  }

  return {
    selected: defaultValue,
    defaultValue: isData ? tempValue : [],
  };
};

class UPickerStack extends React.PureComponent<PickerStackProps, PickerStackState> {
  state = {
    dataSource: [],
    ...getValue(this.props),
  };

  passiveRefresh: (callback: (value: string[]) => void) => void;

  gupValue: string;

  static getDerivedStateFromProps(nextProps: PickerStackProps, state: PickerStackState) {
    if ('value' in nextProps && !deepAsset(nextProps.value, state.prevValue)) {
      return {
        prevValue: nextProps.value,
        ...getValue(nextProps, state.dataSource),
      };
    }

    return null;
  }

  componentDidMount() {
    this.passiveRefresh = passiveRefresh(this.props);
    this.getOptions();
  }

  componentDidUpdate() {
    this.passiveRefresh((value) => {
      this.getOptions({ value: value[0] });
    });
  }

  onOk(selected: any[]) {
    const { onChange, ...rest } = this.props;
    const { _crengine } = this.props;
    const { form, dataBind, path } = _crengine || {};

    if (selected && Array.isArray(selected) && selected.length) {
      const len = selected.length;
      const value = { ...selected[len - 1] };

      if (len > 1) {
        const parents: any[] = [];

        selected.map((item, index) => {
          if (index < len - 1) {
            item = { ...item };

            // 优化数据结构
            if (item.children) {
              delete item.children;
            }

            parents.push(item);
          }

          return item;
        });

        value.parents = parents;
      }

      rest.value = value;

      // 重置 form value
      if (form && value) {
        form.setFieldsValue({
          [getFormId(dataBind, path)]: value,
        });
      }

      this.setState({
        selected: value,
        defaultValue: selected,
      });

      onChange && onChange(rest);
    }
  }

  // 获取数据
  getOptions(param: { [key: string]: any } = {}) {
    const { initFetch, fetchCache, fetchOptsMember, _crengine } = this.props;
    const { name, dataBind } = _crengine || {};

    if (!initFetch) {
      return false;
    }

    // 缓存时间
    const cacheExpire: any = fetchCache && fetchCache.expire ? fetchCache.expire : '';
    const cacheParams: any = fetchCache && fetchCache.params ? fetchCache.params : null;
    // 缓存名字
    let cacheName = dataBind || name || '';

    if (fetchCache && fetchCache.name) {
      cacheName = fetchCache.name;
    }

    const defaultOption = {
      method: 'GET',
      ...initFetch,
      data: {
        ...param,
        ...initFetch.data,
      },
    };

    createModel(`pickerStack_async_${cacheName}`, defaultOption, cacheExpire, cacheParams).execute((result) => {
      if (result && result.code === 0 && result.result) {
        const { options, ...others } = result.result;
        let dataSource = options;
        const dataName = fetchOptsMember || cacheName;

        // dataSource值
        if (dataName && result.result[dataName]) {
          dataSource = others[dataName];
          delete others[dataName];
        }

        this.setState({
          dataSource,
          ...others,
          ...getValue(this.props, dataSource),
        });
      }
    });
  }

  // 数据格式，options、initFetch请求数据
  getDataSource() {
    const { dataSource } = this.state;
    const { options } = this.props;

    if (options && Array.isArray(options) && options.length) {
      return options;
    }

    if (dataSource && Array.isArray(dataSource) && dataSource.length) {
      return dataSource;
    }

    return [];
  }

  // 验证职业等级限制
  validateLevel(cols: any[]) {
    const { min = 1, max } = this.props;

    if (cols && Array.isArray(cols) && cols.length) {
      const current = cols[cols.length - 1];

      if (current && current.level && ((min && current.level < min) || (max && current.level > max))) {
        Toast.show('该职业不可投保此产品/计划');
        return `本产品仅支持${min}-${max}类职业，您无法投保`;
      }
    }

    return '';
  }

  displayRender(selected: any[], reverse?: boolean) {
    const { showAll, showCode } = this.props;

    if (!(selected && Array.isArray(selected) && selected.length)) {
      return null;
    }

    if (showAll) {
      const label: string[] = [];

      selected.map((item) => {
        if (reverse) {
          label.push(item.label);
        } else {
          label.unshift(item.label);
        }
        return item;
      });

      return label.join('');
    }

    if (showCode) {
      let code = '';

      selected.forEach((item) => {
        if (item.code) {
          code = item.code;
        }
      });

      return code;
    }

    return selected[selected.length - 1].label;
  }

  renderContent(props) {
    const { defaultValue } = this.state;
    const { onCancel, onMaskClick } = this.props;
    const { readOnly } = props;

    if (readOnly) {
      return <span>{defaultValue && Array.isArray(defaultValue) && defaultValue.length ? defaultValue.map((item) => item.label).join('') : ''}</span>;
    }

    // 禁止删除<>，不然form会提示异常
    return (
      <>
        <StackPicker
          {...props}
          displayRender={(selected: any[]) => this.displayRender(selected)}
          validate={(cols: any[]) => this.validateLevel(cols)}
          dataSource={this.getDataSource()}
          onOk={(selected: any[]) => this.onOk(deepCopy(selected))}
          onCancel={() => onCancel && onCancel()}
          onMaskClick={() => onMaskClick && onMaskClick()}
        />
      </>
    );
  }

  render() {
    const {
      type,
      value,
      defaultValue,
      placeholder,
      readOnly,
      readonly,
      _crengine,
      prefixCls = 'crm-picker-stack',
      options,
      className,
      valueMember = 'value',
      dataBind,
      fetchOptsMember,
      fetchCache,
      initFetch,
      min = 1,
      max,
      visible,
      showAll,
      submitType,
      ...rest
    } = this.props;
    const { defaultValue: initialValue, selected } = this.state;
    const pickerValues = initialValue && Array.isArray(initialValue) && initialValue.length ? initialValue.map((item) => item[valueMember]) : [];
    const cls = classnames(prefixCls, className);

    const stackProps = clearObj({
      type,
      value: pickerValues,
      showAll,
      placeholder,
      className: cls,
      valueMember,
      min,
      max,
      visible,
      readOnly: readOnly || readonly,
      submitType,
    });

    return (
      <UItem {...rest} hasArrow={!(readOnly || readonly)} itemType="pickerStack" _crengine={_crengine} initialValue={selected}>
        {this.renderContent(stackProps)}
      </UItem>
    );
  }
}

export default UPickerStack;
